import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { Outlet } from '@/lib/router-components';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { Breadcrumb } from '../components/Breadcrumb';
import { TagBadges } from '../components/TagBadge';
import { RecentActivities } from '../components/RecentActivities';
import { ClientFormModal } from '../components/ClientFormModal';
import { TasksByTypeModal } from '../components/TasksByTypeModal';
import { TaskIcon } from '../components/icons/TaskIcon';
import { HearingIcon } from '../components/icons/HearingIcon';
import { DeadlineIcon } from '../components/icons/DeadlineIcon';
import { EventIcon } from '../components/icons/EventIcon';
import { ClientDto, TagDto, PaginatedClientsResponse, ProcessDto, MemberDto, UpdateClientDto, TaskDto, TaskType } from '@prometeus/contracts';
import { formatCPF, formatCNPJ } from '../lib/validations';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { useWorkspaceStore } from '../stores/workspace.store';
import { useAuthStore } from '../stores/auth.store';

function EditClientModal({ 
  workspaceId, 
  clientId, 
  clients, 
  onClose, 
  onSuccess 
}: { 
  workspaceId: string; 
  clientId: string | null; 
  clients: ClientDto[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const queryClient = useQueryClient();
  const clientToEdit = clientId ? clients.find((c) => c.id === clientId) : null;

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateClientDto) => {
      const response = await api.put(`/clients/${clientId}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] });
      onSuccess();
      onClose();
    },
  });

  if (!clientId || !clientToEdit) return null;

  return (
    <ClientFormModal
      workspaceId={workspaceId}
      client={clientToEdit}
      onClose={onClose}
      onSave={(data) => updateMutation.mutate(data)}
      isSaving={updateMutation.isPending}
    />
  );
}

export const Route = createFileRoute('/w/$workspaceId/clients')({
  component: ClientsLayout,
});

function ClientsLayout() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on an exact match (not a child route)
  const isIndex = location.pathname === `/w/${workspaceId}/clients`;

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [openModalType, setOpenModalType] = useState<TaskType | null>(null);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const limit = 5;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const canRead = hasPermission('clients.read');
  const workspaceIdStore = useWorkspaceStore((state) => state.currentWorkspace?.id);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (isIndex && !isLoadingPermissions && !canRead) {
      showPermissionDenied('Ver clientes');
      navigate({ to: '/w/$workspaceId/agenda', params: { workspaceId } });
    }
  }, [location.pathname, isIndex, isLoadingPermissions, canRead, showPermissionDenied, navigate, workspaceId]);

  // Fetch tags for filter
  const { data: allTags } = useQuery<TagDto[]>({
    queryKey: ['tags', workspaceId],
    queryFn: async () => {
      const response = await api.get('/tags');
      return response.data.data || [];
    },
    enabled: !!workspaceId && (isLoadingPermissions || canRead),
  });

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(searchInput);
    }, 500); // 500ms delay for smoother search

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Build query params
  const queryParams = useMemo(() => {
    const params: any = {
      page,
      limit,
    };
    if (search.trim()) {
      params.search = search.trim();
    }
    if (selectedTagIds.length > 0) {
      params.tagIds = selectedTagIds.join(',');
    }
    if (includeArchived) {
      params.includeArchived = 'true';
    }
    return params;
  }, [page, search, selectedTagIds, limit, includeArchived]);

  const {
    data: clientsData,
    isLoading,
    isFetching,
    isPlaceholderData,
  } = useQuery<PaginatedClientsResponse>({
    queryKey: ['clients', workspaceId, queryParams],
    queryFn: async () => {
      const response = await api.get('/clients', { params: queryParams });
      return response.data.data;
    },
    enabled: !!workspaceId && (isLoadingPermissions || canRead),
    placeholderData: (previousData) => previousData,
  });

  const clients = clientsData?.data || [];
  const totalPages = clientsData?.totalPages || 0;
  const total = clientsData?.total || 0;

  // Fetch active clients count (global, not paginated)
  const { data: activeClientsData } = useQuery<PaginatedClientsResponse>({
    queryKey: ['clients', workspaceId, 'active-count'],
    queryFn: async () => {
      const response = await api.get('/clients', { 
        params: { 
          page: 1, 
          limit: 1,
          includeArchived: 'false'
        } 
      });
      return response.data.data;
    },
    enabled: !!workspaceId && (isLoadingPermissions || canRead),
  });

  const activeClientsCount = activeClientsData?.total || 0;

  // Reset to page 1 when search or tags change
  useEffect(() => {
    setPage(1);
  }, [search, selectedTagIds.join(',')]);

  // Fechar dropdown de filtro ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setFilterDropdownOpen(false);
      }
    };
    if (filterDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [filterDropdownOpen]);

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
    enabled: !!workspaceId && (isLoadingPermissions || canRead),
  });

  // Fetch all members for the workspace
  const { data: allMembers } = useQuery<MemberDto[]>({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      try {
        const response = await api.get('/members');
        return response.data.data || [];
      } catch {
        return [];
      }
    },
    enabled: !!workspaceId && (isLoadingPermissions || canRead),
  });

  // Get current user's memberId for tasks
  const currentMember = allMembers?.find((m: any) => m.userId === user?.id);
  const currentMemberId = currentMember?.id;

  // Calculate date range for the selected date
  const dateStart = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [selectedDate]);

  const dateEnd = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [selectedDate]);

  // Fetch all tasks for the selected date
  const { data: allTasksForDate } = useQuery<TaskDto[]>({
    queryKey: ['tasks-by-date', workspaceId, dateStart.toISOString(), currentMemberId],
    queryFn: async () => {
      if (!workspaceId || !currentMemberId) return [];
      
      const response = await api.get('/tasks', {
        params: {
          assignee: currentMemberId,
          dateFrom: dateStart.toISOString(),
          dateTo: dateEnd.toISOString(),
        },
      });
      return response.data.data;
    },
    enabled: !!workspaceId && !!currentMemberId && (isLoadingPermissions || canRead),
  });

  // Calculate counts by type
  const taskCounts = useMemo(() => {
    if (!allTasksForDate) {
      return { TASK: 0, DEADLINE: 0, EVENT: 0, HEARING: 0 };
    }

    // Filter tasks that match the selected date based on their type
    const filteredTasks = allTasksForDate.filter((task) => {
      const taskDate = new Date();
      
      if (task.type === 'TASK' || task.type === 'DEADLINE') {
        // Check securityDeadline, fatalDeadline, or dueDate
        const deadlineDate = task.securityDeadline || task.fatalDeadline || task.dueDate;
        if (!deadlineDate) return false;
        taskDate.setTime(new Date(deadlineDate).getTime());
      } else if (task.type === 'EVENT' || task.type === 'HEARING') {
        // Check startAt or endAt
        const eventDate = task.startAt || task.endAt;
        if (!eventDate) return false;
        taskDate.setTime(new Date(eventDate).getTime());
      } else {
        return false;
      }

      // Compare only date (ignore time)
      return (
        taskDate.getDate() === selectedDate.getDate() &&
        taskDate.getMonth() === selectedDate.getMonth() &&
        taskDate.getFullYear() === selectedDate.getFullYear()
      );
    });

    return {
      TASK: filteredTasks.filter((t) => t.type === 'TASK').length,
      DEADLINE: filteredTasks.filter((t) => t.type === 'DEADLINE').length,
      EVENT: filteredTasks.filter((t) => t.type === 'EVENT').length,
      HEARING: filteredTasks.filter((t) => t.type === 'HEARING').length,
    };
  }, [allTasksForDate, selectedDate]);

  // Date navigation functions
  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // If we're on a child route (like /new or /$clientId), render the Outlet
  if (!isIndex) {
    return <Outlet />;
  }

  if (!isLoadingPermissions && !canRead) return null;

  // Otherwise, render the clients list (fullscreen loading só na carga inicial sem dados)
  if (isLoading && !clientsData) {
    return <div className="p-8">Carregando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/workspaces' },
            { label: 'Clientes' },
          ]}
        />

        {/* Tasks Overview Container - mesmo padrão visual do container de clientes e feed */}
        <div
          className="mt-6 bg-white dark:bg-gray-800 p-6"
          style={{ borderRadius: 15, boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}
        >
          {/* Date Navigator */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={goToPreviousDay}
                className="flex items-center justify-center transition-colors hover:opacity-80"
                style={{
                  width: 23,
                  height: 23,
                  borderRadius: 5,
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  backgroundColor: '#06141B',
                  color: '#F5F6F5',
                  boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                }}
                title="Dia anterior"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="text-center">
                <p className="text-[14px] font-normal capitalize" style={{ fontFamily: "'Poppins', sans-serif", color: '#06141B' }}>
                  {formatDateDisplay(selectedDate)}
                </p>
              </div>

              <button
                type="button"
                onClick={goToNextDay}
                className="flex items-center justify-center transition-colors hover:opacity-80"
                style={{
                  width: 23,
                  height: 23,
                  borderRadius: 5,
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  backgroundColor: '#06141B',
                  color: '#F5F6F5',
                  boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                }}
                title="Próximo dia"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {!isToday(selectedDate) && (
              <button
                type="button"
                onClick={goToToday}
                className="px-4 py-2 rounded-[10px] transition-colors hover:opacity-80"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 400,
                  backgroundColor: '#06141B',
                  color: '#F5F6F5',
                  boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                }}
              >
                Hoje
              </button>
            )}
          </div>

          {/* Task Count Cards - mesmo padrão dos itens da lista (card com sombra) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Tarefas */}
            <button
              type="button"
              onClick={() => setOpenModalType('TASK')}
              className="p-3 transition-all cursor-pointer rounded-[15px] hover:opacity-90"
              style={{
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: '#FAFAFA',
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.07)',
              }}
            >
              <div className="flex items-center">
                <div className="flex items-center justify-center mr-3" style={{ color: '#06141B' }}>
                  <TaskIcon className="w-[18px] h-[18px]" />
                </div>
                <div className="flex flex-col flex-1 items-center text-center">
                  <p className="text-[12px] mb-1" style={{ color: '#06141B' }}>
                    Tarefas
                  </p>
                  <p className="text-[14px] font-bold" style={{ color: '#06141B' }}>
                    {taskCounts.TASK}
                  </p>
                </div>
              </div>
            </button>

            {/* Audiências */}
            <button
              type="button"
              onClick={() => setOpenModalType('HEARING')}
              className="p-3 transition-all cursor-pointer rounded-[15px] hover:opacity-90"
              style={{
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: '#FAFAFA',
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.07)',
              }}
            >
              <div className="flex items-center">
                <div className="flex items-center justify-center mr-3" style={{ color: '#06141B' }}>
                  <HearingIcon className="w-[18px] h-[18px]" />
                </div>
                <div className="flex flex-col flex-1 items-center text-center">
                  <p className="text-[12px] mb-1" style={{ color: '#06141B' }}>
                    Audiências
                  </p>
                  <p className="text-[14px] font-bold" style={{ color: '#06141B' }}>
                    {taskCounts.HEARING}
                  </p>
                </div>
              </div>
            </button>

            {/* Prazos */}
            <button
              type="button"
              onClick={() => setOpenModalType('DEADLINE')}
              className="p-3 transition-all cursor-pointer rounded-[15px] hover:opacity-90"
              style={{
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: '#FAFAFA',
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.07)',
              }}
            >
              <div className="flex items-center">
                <div className="flex items-center justify-center mr-3" style={{ color: '#06141B' }}>
                  <DeadlineIcon className="w-[18px] h-[18px]" />
                </div>
                <div className="flex flex-col flex-1 items-center text-center">
                  <p className="text-[12px] mb-1" style={{ color: '#06141B' }}>
                    Prazos
                  </p>
                  <p className="text-[14px] font-bold" style={{ color: '#06141B' }}>
                    {taskCounts.DEADLINE}
                  </p>
                </div>
              </div>
            </button>

            {/* Eventos */}
            <button
              type="button"
              onClick={() => setOpenModalType('EVENT')}
              className="p-3 transition-all cursor-pointer rounded-[15px] hover:opacity-90"
              style={{
                fontFamily: "'Poppins', sans-serif",
                backgroundColor: '#FAFAFA',
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.07)',
              }}
            >
              <div className="flex items-center">
                <div className="flex items-center justify-center mr-3" style={{ color: '#06141B' }}>
                  <EventIcon className="w-[18px] h-[18px]" />
                </div>
                <div className="flex flex-col flex-1 items-center text-center">
                  <p className="text-[12px] mb-1" style={{ color: '#06141B' }}>
                    Eventos
                  </p>
                  <p className="text-[14px] font-bold" style={{ color: '#06141B' }}>
                    {taskCounts.EVENT}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Modals */}
        {openModalType && (
          <TasksByTypeModal
            isOpen={!!openModalType}
            onClose={() => setOpenModalType(null)}
            type={openModalType}
            date={selectedDate}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_520px] gap-6 mt-6">
        <div>
          <div className="bg-white dark:bg-gray-800 relative" style={{ borderRadius: 15, boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}>
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="mb-1" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: 400, color: '#06141B' }}>Clientes</h1>
                <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', fontWeight: 400, color: '#4A5C6A' }}>
                  {activeClientsCount} {activeClientsCount === 1 ? 'cliente ativo' : 'clientes ativos'}
                </p>
              </div>
              <div className="flex items-center gap-3" ref={filterDropdownRef}>
                {/* Search Input */}
                <div className="relative w-[250px]">
                  <input
                    type="text"
                    placeholder="Busca por cliente"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-0 placeholder-[#CCD0CF]"
                    style={{ 
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#06141B',
                      backgroundColor: '#F9FAFB',
                      borderRadius: 10,
                      outline: 'none',
                      boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.outline = 'none';
                    }}
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none"
                    fill="none"
                    stroke="#CCD0CF"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {/* Botão Filtro */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setFilterDropdownOpen((v) => !v)}
                    className="w-8 h-8 flex items-center justify-center transition-colors hover:opacity-80"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      backgroundColor: '#06141B',
                      fontWeight: 400,
                      borderRadius: 10,
                      boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                    }}
                    title="Filtros"
                  >
                    <img src="/filtro.svg" alt="Filtro" className="w-3 h-3 object-contain" />
                  </button>
                  {filterDropdownOpen && (
                    <div
                      className="absolute right-0 top-full mt-2 z-50 min-w-[200px] py-2 rounded-[10px] border border-gray-200 dark:border-gray-600"
                      style={{
                        backgroundColor: '#fff',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-xs font-medium" style={{ color: '#06141B', fontSize: 12 }}>Tags</span>
                      </div>
                      {allTags && allTags.length > 0 ? (
                        <div className="max-h-48 overflow-y-auto">
                          {allTags.map((tag) => (
                            <label
                              key={tag.id}
                              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedTagIds.includes(tag.id)}
                                onChange={() => {
                                  setSelectedTagIds((prev) =>
                                    prev.includes(tag.id) ? prev.filter((id) => id !== tag.id) : [...prev, tag.id]
                                  );
                                }}
                                className="rounded border-gray-300"
                              />
                              <span className="text-xs" style={{ color: '#06141B', fontSize: 12 }}>{tag.name}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className="px-3 py-2 text-xs text-gray-500" style={{ fontSize: 12 }}>Nenhuma tag cadastrada</div>
                      )}
                      <div className="border-t border-gray-100 dark:border-gray-700 mt-1 pt-2 px-3">
                        <label className="flex items-center gap-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeArchived}
                            onChange={(e) => setIncludeArchived(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <span className="text-xs" style={{ color: '#06141B', fontSize: 12 }}>Arquivados</span>
                        </label>
                      </div>
                      <div className="border-t border-gray-100 dark:border-gray-700 mt-1 pt-2 px-3">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTagIds([]);
                            setIncludeArchived(false);
                            setFilterDropdownOpen(false);
                          }}
                          className="w-full text-left py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 rounded transition-colors"
                          style={{ color: '#06141B', fontSize: 12 }}
                        >
                          Limpar filtros
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Botão Adicionar Cliente */}
                <button
                  onClick={() => {
                    if (!requirePermission('clients.create', 'Criar clientes')) return;
                    navigate({ to: `/w/${workspaceId}/clients/new` });
                  }}
                  className="w-8 h-8 flex items-center justify-center transition-colors hover:opacity-80"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '18px',
                    backgroundColor: '#06141B',
                    color: '#F5F6F5',
                    fontWeight: 400,
                    borderRadius: 10,
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                  }}
                  title="Novo Cliente"
                >
                  +
                </button>
              </div>
            </div>

            {/* Clients List - altura mínima para o container manter o mesmo tamanho */}
            <div style={{ minHeight: 380 }}>
            {isFetching && clients.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : clients.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Nenhum cliente encontrado</div>
            ) : (
              <>
                <div
                  className={`overflow-x-auto transition-opacity duration-200 ${isFetching && isPlaceholderData ? 'opacity-60' : ''}`}
                  style={{ overflowY: 'visible', position: 'relative', paddingLeft: 12, paddingRight: 12, paddingBottom: 14 }}
                >
                  {/* Header */}
                  <div
                    className="w-full mb-2"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '45% 20% 20% 15%',
                      alignItems: 'center',
                    }}
                  >
                    <div className="text-left py-3 px-4 text-xs uppercase tracking-wider flex items-center" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      CLIENTE
                    </div>
                    <div className="text-center py-3 px-4 text-xs uppercase tracking-wider flex items-center justify-center" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      MEMBROS
                    </div>
                    <div className="text-left py-3 px-4 text-xs uppercase tracking-wider flex items-center" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                      STATUS
                    </div>
                    <div className="text-left py-3 px-4 text-xs uppercase tracking-wider flex items-center" style={{ fontFamily: 'Poppins, sans-serif', color: '#9CA3AF', fontWeight: 400 }}>
                    </div>
                  </div>
                  {/* Lista - cada item é um único container com sombra */}
                  <div className="w-full flex flex-col" style={{ gap: 14 }}>
                    {clients.map((client: ClientDto) => (
                      <ClientListItem
                        key={client.id}
                        client={client}
                        workspaceId={workspaceId}
                        allMembers={allMembers || []}
                        onEdit={() => setEditingClientId(client.id)}
                        onClick={() => navigate({ to: `/w/${workspaceId}/clients/${client.id}` })}
                      />
                    ))}
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-6">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ 
                        fontFamily: 'Poppins, sans-serif', 
                        fontSize: '14px', 
                        fontWeight: 500,
                        backgroundColor: '#F9FAFB',
                        color: '#2D3748',
                      }}
                      onMouseEnter={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.backgroundColor = '#F3F4F6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#F9FAFB';
                      }}
                    >
                      &lt;
                    </button>
                    <span className="px-4 py-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
                      Página {page} de {totalPages} ({total} {total === 1 ? 'cliente' : 'clientes'})
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ 
                        fontFamily: 'Poppins, sans-serif', 
                        fontSize: '14px', 
                        fontWeight: 500,
                        backgroundColor: '#F9FAFB',
                        color: '#2D3748',
                      }}
                      onMouseEnter={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.backgroundColor = '#F3F4F6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#F9FAFB';
                      }}
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </>
            )}
            </div>
          </div>
        </div>
        </div>
        
        {/* Recent Activities Sidebar - mesma altura do container de clientes */}
        <div className="lg:sticky lg:top-8 lg:self-start h-full min-h-0">
          <RecentActivities workspaceId={workspaceId} />
        </div>
        </div>
      </div>

      {/* Edit Client Modal */}
      <EditClientModal
        workspaceId={workspaceId}
        clientId={editingClientId}
        clients={clients}
        onClose={() => setEditingClientId(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] });
        }}
      />
    </div>
  );
}

function MemberAvatar({ 
  member, 
  index, 
  totalMembers 
}: { 
  member: MemberDto; 
  index: number; 
  totalMembers: number;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const getInitials = (name: string | null | undefined): string => {
    if (!name || typeof name !== 'string' || name.length === 0) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  useEffect(() => {
    if (!showTooltip || !triggerRef.current) {
      setTooltipPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, [showTooltip]);

  const tooltipContent = (
    <div
      className="fixed z-[100001] px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none"
      style={{
        top: tooltipPosition?.top ?? 0,
        left: tooltipPosition?.left ?? 0,
        transform: 'translate(-50%, -100%)',
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {member.user?.name}
      <div className="absolute left-1/2 -bottom-1 w-2 h-2 bg-gray-900 transform -translate-x-1/2 rotate-45" />
    </div>
  );

  return (
    <div
      ref={triggerRef}
      className="relative flex-shrink-0"
      style={{ 
        marginLeft: index > 0 ? '-6px' : 0, 
        zIndex: totalMembers - index 
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {member.user?.avatarUrl && member.user.avatarUrl.trim() !== '' && !avatarError ? (
        <>
          <img
            src={member.user.avatarUrl}
            alt={member.user.name}
            className="rounded-full object-cover cursor-pointer transition-all border-2 flex-shrink-0"
            style={{
              width: 25,
              height: 25,
              borderColor: '#F5F6F5',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#2D3748';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#F5F6F5';
            }}
            onError={() => setAvatarError(true)}
          />
          {showTooltip && tooltipPosition && createPortal(tooltipContent, document.body)}
        </>
      ) : (
        <>
          <div
            className="rounded-full flex items-center justify-center text-xs font-medium cursor-pointer transition-all border-2 flex-shrink-0"
            style={{
              width: 25,
              height: 25,
              backgroundColor: '#06141B',
              color: '#CCD0CF',
              borderColor: '#F5F6F5',
              fontFamily: "'Poppins', sans-serif",
              textTransform: 'lowercase',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#2D3748';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#F5F6F5';
            }}
          >
            {getInitials(member.user?.name).toLowerCase()}
          </div>
          {showTooltip && tooltipPosition && createPortal(tooltipContent, document.body)}
        </>
      )}
    </div>
  );
}

function ClientListItem({ 
  client, 
  workspaceId, 
  allMembers, 
  onEdit,
  onClick 
}: { 
  client: ClientDto; 
  workspaceId: string; 
  allMembers: MemberDto[];
  onEdit: () => void;
  onClick: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const { requirePermission } = usePermissionGuard();

  const DROPDOWN_WIDTH = 192; // w-48

  // Calcular posição do dropdown quando abre (para renderizar em portal fora do scroll)
  useEffect(() => {
    if (!menuOpen || !triggerButtonRef.current) {
      setDropdownPosition(null);
      return;
    }
    const rect = triggerButtonRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 8,
      left: rect.right - DROPDOWN_WIDTH,
    });
  }, [menuOpen]);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-client-dropdown]')) {
          setMenuOpen(false);
        }
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Fechar ao rolar (evita dropdown fixo desalinhado)
  useEffect(() => {
    if (!menuOpen) return;
    const handleScroll = () => setMenuOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [menuOpen]);

  const archiveMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/clients/${client.id}/archive`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] });
      setMenuOpen(false);
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/clients/${client.id}/favorite`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', workspaceId] });
    },
  });

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!requirePermission('clients.archive', 'Arquivar clientes')) return;
    archiveMutation.mutate();
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!requirePermission('clients.update', 'Editar clientes')) return;
    onEdit();
    setMenuOpen(false);
  };
  // Query processes for this client
  const { data: processes } = useQuery<ProcessDto[]>({
    queryKey: ['processes', workspaceId, client.id],
    queryFn: async () => {
      try {
        const response = await api.get(`/clients/${client.id}/processes`);
        return response.data.data || [];
      } catch {
        return [];
      }
    },
  });

  // Get unique responsible member IDs from processes
  const responsibleMemberIds = useMemo(() => {
    if (!processes) return [];
    const memberIds = processes
      .map(p => p.responsibleMemberId)
      .filter((id): id is string => id !== null && id !== undefined);
    return [...new Set(memberIds)];
  }, [processes]);

  // Get member objects for the responsible members
  const responsibleMembers = useMemo(() => {
    return allMembers.filter(member => responsibleMemberIds.includes(member.id));
  }, [allMembers, responsibleMemberIds]);

  // Get processes count
  const processesCount = processes?.length || 0;

  // Format document
  const formattedDocument = useMemo(() => {
    if (!client.document) return '-';
    return client.type === 'INDIVIDUAL' 
      ? formatCPF(client.document)
      : formatCNPJ(client.document);
  }, [client.document, client.type]);

  // Get avatar URL or use placeholder
  const avatarUrl = client.avatarUrl || null;

  return (
    <div
      onClick={onClick}
      className="cursor-pointer transition-colors w-full"
      style={{
        display: 'grid',
        gridTemplateColumns: '45% 20% 20% 15%',
        alignItems: 'center',
        backgroundColor: '#FAFAFA',
        borderRadius: 15,
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.07)',
      }}
    >
      <div className="py-1.5 px-3 flex items-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', minHeight: 56 }}>
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              favoriteMutation.mutate();
            }}
            className="flex-shrink-0 p-1 hover:opacity-70 transition-opacity"
            title={client.isFavorite ? 'Desfavoritar' : 'Favoritar'}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={client.isFavorite ? '#FBBF24' : 'none'}
              stroke={client.isFavorite ? '#FBBF24' : '#9CA3AF'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
          {avatarUrl ? (
            <img 
              src={avatarUrl} 
              alt={client.name}
              className="flex-shrink-0 object-cover"
              style={{ width: 40, height: 40, borderRadius: 10 }}
            />
          ) : (
            <div 
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                backgroundColor: '#06141B',
              }}
            >
              <span
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#F5F6F5',
                  textTransform: 'lowercase',
                }}
              >
                {(client.name || 'c').charAt(0).toLowerCase()}
              </span>
            </div>
          )}
          <span className="truncate" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#2E2D30' }}>
            {client.name}
          </span>
        </div>
      </div>
      <div className="py-1.5 px-3 flex items-center justify-center" style={{ overflow: 'visible' }}>
        <div className="flex items-center justify-center">
          {responsibleMembers.length > 0 ? (
            responsibleMembers.map((member, index) => (
              <MemberAvatar
                key={member.id}
                member={member}
                index={index}
                totalMembers={responsibleMembers.length}
              />
            ))
          ) : (
            <span className="text-gray-400" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px' }}>
              -
            </span>
          )}
        </div>
      </div>
      <div className="py-1.5 px-3 flex items-center" style={{ overflow: 'hidden' }}>
        <span 
          style={{ 
            fontFamily: 'Poppins, sans-serif',
            fontSize: '12px',
            color: client.archivedAt ? '#9CA3AF' : '#2E2D30',
            fontWeight: 400
          }}
        >
          {client.archivedAt ? 'Arquivado' : 'Ativo'}
        </span>
      </div>
      <div className="py-1.5 px-3 flex items-center justify-end" style={{ overflow: 'visible', position: 'relative', paddingRight: '12px' }}>
        <div className="relative" ref={menuRef}>
          <button
            ref={triggerButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            style={{ color: '#A0AEC0' }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="5" r="1.5" fill="currentColor"/>
              <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
              <circle cx="10" cy="15" r="1.5" fill="currentColor"/>
            </svg>
          </button>
          {menuOpen && dropdownPosition && createPortal(
            <div
              data-client-dropdown
              className="fixed w-48 bg-white rounded-lg shadow-lg py-1 border border-gray-200"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                zIndex: 9999,
              }}
            >
              <button
                type="button"
                onClick={handleEdit}
                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
              >
                Editar cliente
              </button>
              <button
                type="button"
                onClick={handleArchive}
                className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: 400 }}
                disabled={archiveMutation.isPending}
              >
                {client.archivedAt ? 'Desarquivar cliente' : 'Arquivar cliente'}
              </button>
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
}

